/**
 * The quote write contracts: the bodies of the platform's quote routes, one Zod schema each, built from the field
 * types. The platform's DTOs are these schemas (platform/src/resources/order-quotes/*.dto.ts); Birdie parses each
 * quote write with them before it sends it. Limits and value lists that every surface shows live here once.
 *
 * No schema here carries a `.refine()`: Zod 4.5.4 refuses `.pick()`, `.omit()`, `.partial()` and `.merge()` on an object
 * with a refinement, and `.extend()` when it overwrites one of its keys. A route that needs a cross-field rule adds its
 * refine on top of the contract.
 *
 * PURE.
 */
import { z } from "zod";
import {
  type ChoiceWords,
  choiceSchema,
  customerMessageSchema,
  longTextSchema,
  textSchema,
} from "./field-types";

/** A quote offers at most this many options. */
export const MAX_QUOTE_OPTIONS = 5;
/** Longest quote name, in characters. */
export const QUOTE_NAME_MAX = 120;
/** Longest quote description (markdown), in characters. */
export const QUOTE_DESCRIPTION_MAX = 5000;
/** Longest option name, in characters. */
export const QUOTE_OPTION_NAME_MAX = 40;
/** Longest option description, in characters. */
export const QUOTE_OPTION_DESCRIPTION_MAX = 500;

export const QUOTE_PRICE_APPEARANCES = ["ITEMIZED", "ROLLUP", "TOTAL"] as const;
export type QuotePriceAppearance = (typeof QUOTE_PRICE_APPEARANCES)[number];

/**
 * The words for each price display: `label` on the office's radio cards, `choiceLabel` on a Birdie card's select,
 * `helper` the sentence under either.
 */
export const QUOTE_PRICE_APPEARANCE_META: Readonly<
  Record<
    QuotePriceAppearance,
    {
      readonly label: string;
      readonly choiceLabel: string;
      readonly helper: string;
    }
  >
> = {
  ITEMIZED: {
    label: "Itemized",
    choiceLabel: "Itemized: every line with its price",
    helper:
      "Every line item shows its own price, with a group subtotal and a total.",
  },
  ROLLUP: {
    label: "Rollup",
    choiceLabel: "Rollup: group subtotals, no line prices",
    helper:
      "Line items show quantities only; each group shows its subtotal and the quote shows a total.",
  },
  TOTAL: {
    label: "Total",
    choiceLabel: "Total only",
    helper:
      "Line items and groups show no prices; the customer sees only the total.",
  },
};

const PRICE_APPEARANCE_CHOICE_WORDS = {
  ITEMIZED: {
    label: QUOTE_PRICE_APPEARANCE_META.ITEMIZED.choiceLabel,
    hint: QUOTE_PRICE_APPEARANCE_META.ITEMIZED.helper,
  },
  ROLLUP: {
    label: QUOTE_PRICE_APPEARANCE_META.ROLLUP.choiceLabel,
    hint: QUOTE_PRICE_APPEARANCE_META.ROLLUP.helper,
  },
  TOTAL: {
    label: QUOTE_PRICE_APPEARANCE_META.TOTAL.choiceLabel,
    hint: QUOTE_PRICE_APPEARANCE_META.TOTAL.helper,
  },
} satisfies Record<QuotePriceAppearance, ChoiceWords>;

export const quotePriceAppearanceSchema = choiceSchema(
  QUOTE_PRICE_APPEARANCES,
  PRICE_APPEARANCE_CHOICE_WORDS,
);

export const QUOTE_SOURCES = ["BIRDIE", "DOCUMENT", "MANUAL"] as const;
export const quoteSourceSchema = z.enum(QUOTE_SOURCES);
export type QuoteSource = (typeof QUOTE_SOURCES)[number];

/**
 * How `POST /order-quotes/:quoteId/issue` moves a quote: publish and open it,
 * or accept it for the customer. Each has a `_AND_SEND` twin that also posts
 * the quote card with `message` to the customer thread; without it the
 * customer is not messaged (an acceptance is often only recorded, 2026-11-02).
 */
export const QUOTE_ISSUE_MODES = [
  "ISSUE",
  "ISSUE_AND_SEND",
  "ACCEPT_ON_BEHALF",
  "ACCEPT_ON_BEHALF_AND_SEND",
] as const;
export const quoteIssueModeSchema = z.enum(QUOTE_ISSUE_MODES);
export type QuoteIssueMode = (typeof QUOTE_ISSUE_MODES)[number];

/** The mode accepts the quote for the customer, rather than opening it. */
export function quoteIssueAcceptsOnBehalf(mode: QuoteIssueMode): boolean {
  return mode === "ACCEPT_ON_BEHALF" || mode === "ACCEPT_ON_BEHALF_AND_SEND";
}

/** The mode posts the quote card, with `message`, to the customer thread. */
export function quoteIssueSendsCard(mode: QuoteIssueMode): boolean {
  return mode === "ISSUE_AND_SEND" || mode === "ACCEPT_ON_BEHALF_AND_SEND";
}

/**
 * The default name of the option at a display index: "Option A" for 0, "Option B" for 1, …; past "Z" it counts on in
 * numbers. Moved here from platform/src/resources/order-quotes/_calc/quote-options.ts:32 unchanged: Birdie names an
 * unnamed option it adds with it.
 */
export function optionLetterName(index: number): string {
  if (index >= 0 && index < 26) {
    return `Option ${String.fromCharCode(65 + index)}`;
  }
  return `Option ${index + 1}`;
}

/** A quote's name as the platform takes it: not trimmed here (the service trims and falls back). */
export const quoteNameSchema = textSchema.max(QUOTE_NAME_MAX);
/** What the customer reads under the quote's name — markdown. Empty (or null) means none. */
export const quoteDescriptionSchema = longTextSchema
  .trim()
  .max(QUOTE_DESCRIPTION_MAX);
export const quoteOptionNameSchema = textSchema
  .trim()
  .min(1)
  .max(QUOTE_OPTION_NAME_MAX);
export const quoteOptionDescriptionSchema = longTextSchema
  .trim()
  .max(QUOTE_OPTION_DESCRIPTION_MAX);

/** POST /api/v5/order-quotes */
export const quoteCreateWriteSchema = z.object({
  orderId: z.string().min(1, "Order ID is required"),
  source: quoteSourceSchema,
  name: quoteNameSchema.optional(),
});

/** PATCH /api/v5/order-quotes/:quoteId (the route adds its at-least-one refine). */
export const quoteEditWriteSchema = z.object({
  name: quoteNameSchema.optional(),
  description: quoteDescriptionSchema.nullable().optional(),
});

/** POST /api/v5/order-quotes/:quoteId/options */
export const quoteOptionAddWriteSchema = z.object({
  name: quoteOptionNameSchema,
  description: quoteOptionDescriptionSchema.nullable().optional(),
  copyFromOptionId: z.string().min(1).optional(),
  currentOptionName: quoteOptionNameSchema.optional(),
});

/** PATCH /api/v5/order-quotes/:quoteId/options/:optionId (the route adds its at-least-one refine). */
export const quoteOptionUpdateWriteSchema = z.object({
  name: quoteOptionNameSchema.optional(),
  description: quoteOptionDescriptionSchema.nullable().optional(),
  position: z.number().int().min(1).optional(),
});

/** POST /api/v5/order-quotes/:quoteId/issue */
export const quoteIssueWriteSchema = z.object({
  priceAppearance: quotePriceAppearanceSchema,
  /** null = "No expiry" (a complete choice, not a missing one). */
  expiresAt: z.iso.datetime().nullable(),
  mode: quoteIssueModeSchema,
  /** The note above the card, for a `_AND_SEND` mode; an empty string is legal. */
  message: customerMessageSchema.optional(),
  /** ACCEPT_ON_BEHALF only: the option the customer chose. */
  optionId: z.string().min(1).optional(),
});

export type QuoteCreateWrite = z.input<typeof quoteCreateWriteSchema>;
export type QuoteEditWrite = z.input<typeof quoteEditWriteSchema>;
export type QuoteOptionAddWrite = z.input<typeof quoteOptionAddWriteSchema>;
export type QuoteOptionUpdateWrite = z.input<
  typeof quoteOptionUpdateWriteSchema
>;
export type QuoteIssueWrite = z.input<typeof quoteIssueWriteSchema>;
